/**
 * AutoMazen Hero Video JavaScript
 * 
 * Features:
 * - Lazy loading of video content
 * - Responsive video source switching
 * - YouTube and Vimeo API integration
 * - Custom playback controls
 * - Accessibility enhancements
 * - Performance optimizations
 */

(function() {
  // Selectors for all hero video components
  const selectors = {
    section: '[data-section-type="hero-video"]',
    videoContainer: '.automazen-hero-video__video-container',
    video: '.automazen-hero-video__video',
    youtubeWrapper: '.automazen-hero-video__youtube-wrapper',
    vimeoWrapper: '.automazen-hero-video__vimeo-wrapper',
    playPauseButton: '.automazen-hero-video__play-pause-button',
    muteButton: '.automazen-hero-video__mute-button',
    heading: '.automazen-hero-video__heading',
    subheading: '.automazen-hero-video__subheading',
    buttons: '.automazen-hero-video__buttons'
  };
  
  // YouTube API loaded flag
  let youtubeAPILoaded = false;
  
  // Vimeo API loaded flag
  let vimeoAPILoaded = false;
  
  // Store all initialized sections
  const sections = [];
  
  class AutoMazenHeroVideo {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.videoContainer = container.querySelector(selectors.videoContainer);
      this.video = container.querySelector(selectors.video);
      this.youtubeWrapper = container.querySelector(selectors.youtubeWrapper);
      this.vimeoWrapper = container.querySelector(selectors.vimeoWrapper);
      this.playPauseButton = container.querySelector(selectors.playPauseButton);
      this.muteButton = container.querySelector(selectors.muteButton);
      
      // Get settings from data attributes
      this.autoplay = container.dataset.autoplay === 'true';
      this.loop = container.dataset.loop === 'true';
      this.lazyLoad = container.dataset.lazyLoad === 'true';
      this.respectReducedMotion = container.dataset.respectReducedMotion === 'true';
      this.showControls = container.dataset.showControls === 'true';
      
      // Media player instances
      this.youtubePlayer = null;
      this.vimeoPlayer = null;
      
      // Media state
      this.isPlaying = this.autoplay;
      this.isMuted = true;
      
      // Viewport state
      this.isInViewport = false;
      
      // Animation elements
      this.heading = container.querySelector(selectors.heading);
      this.subheading = container.querySelector(selectors.subheading);
      this.buttons = container.querySelector(selectors.buttons);
      
      // Initialize the section
      this.init();
    }
    
    init() {
      // Check if reduced motion is preferred
      if (this.respectReducedMotion && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.autoplay = false;
        return;
      }
      
      // Set up lazy loading observer if needed
      if (this.lazyLoad) {
        this.setupLazyLoading();
      } else {
        this.loadVideo();
      }
      
      // Set up responsive video sources
      this.setupResponsiveVideo();
      
      // Set up custom controls
      this.setupControls();
      
      // Set up animations
      this.setupAnimations();
      
      // Add window resize listener
      this.setupResizeListener();
    }
    
    setupLazyLoading() {
      // Create intersection observer for lazy loading
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      
      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.isInViewport = true;
            this.loadVideo();
            observer.unobserve(entry.target);
          }
        });
      }, options);
      
      // Start observing the video container
      observer.observe(this.videoContainer);
    }
    
    loadVideo() {
      if (this.video) {
        this.setupHTML5Video();
      } else if (this.youtubeWrapper) {
        this.loadYouTubeAPI();
      } else if (this.vimeoWrapper) {
        this.loadVimeoAPI();
      }
    }
    
    setupHTML5Video() {
      if (!this.video) return;
      
      // Set correct source based on screen size
      this.updateVideoSource();
      
      // Set up event listeners for HTML5 video
      this.video.addEventListener('loadedmetadata', () => {
        if (this.autoplay) {
          this.playVideo();
        }
      });
      
      this.video.addEventListener('ended', () => {
        if (!this.loop) {
          this.isPlaying = false;
          this.updatePlayPauseButton();
        }
      });
    }
    
    updateVideoSource() {
      if (!this.video) return;
      
      const isMobile = window.innerWidth < 768;
      const desktopSource = this.video.dataset.desktopVideoUrl;
      const mobileSource = this.video.dataset.mobileVideoUrl;
      
      // Only update if we have both sources and not already set correctly
      if (desktopSource && mobileSource) {
        const currentSrc = this.video.currentSrc;
        const targetSrc = isMobile ? mobileSource : desktopSource;
        
        if (currentSrc !== targetSrc) {
          const wasPlaying = !this.video.paused;
          const currentTime = this.video.currentTime;
          
          // Update all source elements
          const sources = this.video.querySelectorAll('source');
          if (sources.length) {
            sources.forEach(source => {
              if (source.classList.contains('desktop-video-source')) {
                source.src = desktopSource;
              } else if (source.classList.contains('mobile-video-source')) {
                source.src = mobileSource;
              }
            });
          } else {
            // If no source elements, create and add them
            const desktopSourceElement = document.createElement('source');
            desktopSourceElement.src = desktopSource;
            desktopSourceElement.type = 'video/mp4';
            desktopSourceElement.classList.add('desktop-video-source');
            
            const mobileSourceElement = document.createElement('source');
            mobileSourceElement.src = mobileSource;
            mobileSourceElement.type = 'video/mp4';
            mobileSourceElement.classList.add('mobile-video-source');
            mobileSourceElement.media = '(max-width: 767px)';
            
            this.video.appendChild(desktopSourceElement);
            this.video.appendChild(mobileSourceElement);
          }
          
          // Reload the video
          this.video.load();
          
          // Restore playback state
          this.video.addEventListener('loadedmetadata', () => {
            this.video.currentTime = currentTime;
            if (wasPlaying) {
              this.video.play();
            }
          }, { once: true });
        }
      }
    }
    
    loadYouTubeAPI() {
      if (this.youtubeWrapper) {
        if (!youtubeAPILoaded) {
          // Load YouTube API script
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          const firstScriptTag = document.getElementsByTagName('script')[0];
          firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
          
          // Wait for API to load
          window.onYouTubeIframeAPIReady = () => {
            youtubeAPILoaded = true;
            this.initYouTubePlayer();
            
            // Also initialize any other sections waiting for the API
            sections.forEach(section => {
              if (section.youtubeWrapper && !section.youtubePlayer) {
                section.initYouTubePlayer();
              }
            });
          };
        } else {
          // API already loaded, initialize player
          this.initYouTubePlayer();
        }
      }
    }
    
    initYouTubePlayer() {
      if (!this.youtubeWrapper || !youtubeAPILoaded) return;
      
      const isMobile = window.innerWidth < 768;
      const videoId = isMobile && this.youtubeWrapper.dataset.youtubeMobileId
        ? this.youtubeWrapper.dataset.youtubeMobileId
        : this.youtubeWrapper.dataset.youtubeId;
      
      if (!videoId) return;
      
      const playerVars = {
        autoplay: this.autoplay ? 1 : 0,
        controls: this.showControls ? 1 : 0,
        disablekb: !this.showControls ? 1 : 0,
        enablejsapi: 1,
        fs: 0,
        iv_load_policy: 3,
        loop: this.loop ? 1 : 0,
        modestbranding: 1,
        playsinline: 1,
        rel: 0,
        showinfo: 0
      };
      
      if (this.loop) {
        playerVars.playlist = videoId;
      }
      
      const playerElement = this.youtubeWrapper.querySelector('.automazen-hero-video__youtube');
      
      if (playerElement) {
        this.youtubePlayer = new YT.Player(playerElement.id, {
          height: '100%',
          width: '100%',
          videoId: videoId,
          playerVars: playerVars,
          events: {
            onReady: (event) => {
              event.target.mute();
              if (this.autoplay) {
                event.target.playVideo();
              }
              this.updatePlayPauseButton();
            },
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                this.isPlaying = true;
              } else if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
                this.isPlaying = false;
              }
              this.updatePlayPauseButton();
            }
          }
        });
        
        if (this.showControls) {
          playerElement.classList.add('automazen-hero-video__youtube--with-controls');
        }
      }
    }
    
    loadVimeoAPI() {
      if (this.vimeoWrapper) {
        if (!vimeoAPILoaded) {
          // Load Vimeo API script
          const tag = document.createElement('script');
          tag.src = 'https://player.vimeo.com/api/player.js';
          tag.onload = () => {
            vimeoAPILoaded = true;
            this.initVimeoPlayer();
            
            // Also initialize any other sections waiting for the API
            sections.forEach(section => {
              if (section.vimeoWrapper && !section.vimeoPlayer) {
                section.initVimeoPlayer();
              }
            });
          };
          
          const firstScriptTag = document.getElementsByTagName('script')[0];
          firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        } else {
          // API already loaded, initialize player
          this.initVimeoPlayer();
        }
      }
    }
    
    initVimeoPlayer() {
      if (!this.vimeoWrapper || !vimeoAPILoaded || !window.Vimeo) return;
      
      const isMobile = window.innerWidth < 768;
      const videoId = isMobile && this.vimeoWrapper.dataset.vimeoMobileId
        ? this.vimeoWrapper.dataset.vimeoMobileId
        : this.vimeoWrapper.dataset.vimeoId;
      
      if (!videoId) return;
      
      const playerElement = this.vimeoWrapper.querySelector('.automazen-hero-video__vimeo');
      
      if (playerElement) {
        const options = {
          id: videoId,
          loop: this.loop,
          autoplay: this.autoplay,
          muted: true,
          controls: this.showControls,
          responsive: true,
          background: !this.showControls
        };
        
        this.vimeoPlayer = new Vimeo.Player(playerElement.id, options);
        
        this.vimeoPlayer.on('play', () => {
          this.isPlaying = true;
          this.updatePlayPauseButton();
        });
        
        this.vimeoPlayer.on('pause', () => {
          this.isPlaying = false;
          this.updatePlayPauseButton();
        });
        
        this.vimeoPlayer.on('ended', () => {
          if (!this.loop) {
            this.isPlaying = false;
            this.updatePlayPauseButton();
          }
        });
        
        if (this.showControls) {
          playerElement.classList.add('automazen-hero-video__vimeo--with-controls');
        }
      }
    }
    
    setupControls() {
      if (this.playPauseButton) {
        this.updatePlayPauseButton();
        
        this.playPauseButton.addEventListener('click', () => {
          if (this.isPlaying) {
            this.pauseVideo();
          } else {
            this.playVideo();
          }
        });
      }
      
      if (this.muteButton) {
        this.updateMuteButton();
        
        this.muteButton.addEventListener('click', () => {
          if (this.isMuted) {
            this.unmuteVideo();
          } else {
            this.muteVideo();
          }
        });
      }
    }
    
    setupAnimations() {
      // Initial opacity setting
      if (this.heading) {
        this.heading.style.opacity = '0';
        setTimeout(() => {
          this.heading.style.opacity = '1';
          this.heading.style.transition = 'opacity 0.5s ease';
        }, 100);
      }
      
      if (this.subheading) {
        this.subheading.style.opacity = '0';
        setTimeout(() => {
          this.subheading.style.opacity = '1';
          this.subheading.style.transition = 'opacity 0.5s ease 0.3s';
        }, 100);
      }
      
      if (this.buttons) {
        this.buttons.style.opacity = '0';
        setTimeout(() => {
          this.buttons.style.opacity = '1';
          this.buttons.style.transition = 'opacity 0.5s ease 0.6s';
        }, 100);
      }
      
      // Set up scroll effects if enabled
      const scrollEffect = this.container.classList.contains('automazen-hero-video--parallax') ? 'parallax' : 
                         this.container.classList.contains('automazen-hero-video--fade-in') ? 'fade' : 
                         this.container.classList.contains('automazen-hero-video--parallax automazen-hero-video--fade-in') ? 'both' : 'none';
                         
      if (scrollEffect !== 'none') {
        this.setupScrollEffects(scrollEffect);
      }
    }
    
    setupScrollEffects(effectType) {
      // Get parallax speed if available
      const parallaxSpeed = this.container.dataset.parallaxSpeed ? parseFloat(this.container.dataset.parallaxSpeed) / 10 : 0.5;
      
      // Elements affected by scroll
      const videoWrapper = this.container.querySelector('.automazen-hero-video__video-wrapper');
      const imageWrapper = this.container.querySelector('.automazen-hero-video__image-wrapper');
      const contentContainer = this.container.querySelector('.automazen-hero-video__content-container');
      
      // Set initial visible state for fade effect
      if (effectType === 'fade' || effectType === 'both') {
        // Add observer for fade effect
        const fadeObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.container.classList.add('is-visible');
            } else {
              // Optionally remove visible class when out of view
              // this.container.classList.remove('is-visible');
            }
          });
        }, { threshold: 0.1 });
        
        fadeObserver.observe(this.container);
      }
      
      // Add scroll listener for parallax effect
      if (effectType === 'parallax' || effectType === 'both') {
        window.addEventListener('scroll', () => {
          // Get the scroll position
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          
          // Get section position
          const rect = this.container.getBoundingClientRect();
          const offsetTop = rect.top + scrollTop;
          const sectionHeight = rect.height;
          
          // Calculate the relative scroll position within the section
          const relativeScroll = scrollTop - offsetTop;
          const percentScrolled = Math.max(0, Math.min(1, relativeScroll / sectionHeight));
          
          // Apply parallax effect
          if (videoWrapper) {
            videoWrapper.style.transform = `translateY(${percentScrolled * 100 * parallaxSpeed}px)`;
          }
          
          if (imageWrapper) {
            imageWrapper.style.transform = `translateY(${percentScrolled * 100 * parallaxSpeed}px)`;
          }
        });
        
        // Trigger initial calculation
        window.dispatchEvent(new Event('scroll'));
      }
    }
    
    playVideo() {
      if (this.video) {
        this.video.play().then(() => {
          this.isPlaying = true;
          this.updatePlayPauseButton();
        }).catch(error => {
          console.warn('Could not play video:', error);
        });
      } else if (this.youtubePlayer) {
        this.youtubePlayer.playVideo();
        this.isPlaying = true;
        this.updatePlayPauseButton();
      } else if (this.vimeoPlayer) {
        this.vimeoPlayer.play().then(() => {
          this.isPlaying = true;
          this.updatePlayPauseButton();
        }).catch(error => {
          console.warn('Could not play Vimeo video:', error);
        });
      }
    }
    
    pauseVideo() {
      if (this.video) {
        this.video.pause();
        this.isPlaying = false;
      } else if (this.youtubePlayer) {
        this.youtubePlayer.pauseVideo();
        this.isPlaying = false;
      } else if (this.vimeoPlayer) {
        this.vimeoPlayer.pause();
        this.isPlaying = false;
      }
      
      this.updatePlayPauseButton();
    }
    
    muteVideo() {
      if (this.video) {
        this.video.muted = true;
        this.isMuted = true;
      } else if (this.youtubePlayer) {
        this.youtubePlayer.mute();
        this.isMuted = true;
      } else if (this.vimeoPlayer) {
        this.vimeoPlayer.setVolume(0);
        this.isMuted = true;
      }
      
      this.updateMuteButton();
    }
    
    unmuteVideo() {
      if (this.video) {
        this.video.muted = false;
        this.isMuted = false;
      } else if (this.youtubePlayer) {
        this.youtubePlayer.unMute();
        this.youtubePlayer.setVolume(100);
        this.isMuted = false;
      } else if (this.vimeoPlayer) {
        this.vimeoPlayer.setVolume(1);
        this.isMuted = false;
      }
      
      this.updateMuteButton();
    }
    
    updatePlayPauseButton() {
      if (!this.playPauseButton) return;
      
      this.playPauseButton.setAttribute('aria-pressed', this.isPlaying);
      this.playPauseButton.setAttribute('aria-label', this.isPlaying ? 'Pause video' : 'Play video');
    }
    
    updateMuteButton() {
      if (!this.muteButton) return;
      
      this.muteButton.setAttribute('aria-pressed', this.isMuted);
      this.muteButton.setAttribute('aria-label', this.isMuted ? 'Unmute video' : 'Mute video');
    }
    
    setupAnimations() {
      // Add animation classes to headings and buttons
      if (this.heading) {
        this.heading.classList.add('automazen-hero-video__fade-in');
      }
      
      if (this.subheading) {
        this.subheading.classList.add('automazen-hero-video__fade-in', 'automazen-hero-video__delay-1');
      }
      
      if (this.buttons) {
        this.buttons.classList.add('automazen-hero-video__fade-in', 'automazen-hero-video__delay-2');
      }
    }
    
    setupResizeListener() {
      // Debounced resize handler
      let resizeTimeout;
      
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.updateVideoSource();
          
          // Handle YouTube and Vimeo source switching on resize
          if (this.youtubeWrapper) {
            const isMobile = window.innerWidth < 768;
            const videoId = isMobile && this.youtubeWrapper.dataset.youtubeMobileId
              ? this.youtubeWrapper.dataset.youtubeMobileId
              : this.youtubeWrapper.dataset.youtubeId;
            
            if (this.youtubePlayer && videoId) {
              // Get current state
              const wasPlaying = this.isPlaying;
              // Update video ID
              this.youtubePlayer.loadVideoById(videoId);
              // Restore state
              if (!wasPlaying) {
                this.youtubePlayer.pauseVideo();
              }
            }
          }
          
          if (this.vimeoWrapper) {
            // Vimeo source switching requires re-initialization
            // This is a basic implementation - a more robust one would track playback state
            const isMobile = window.innerWidth < 768;
            const currentId = this.vimeoWrapper.dataset.currentVimeoId;
            const targetId = isMobile && this.vimeoWrapper.dataset.vimeoMobileId
              ? this.vimeoWrapper.dataset.vimeoMobileId
              : this.vimeoWrapper.dataset.vimeoId;
            
            if (currentId !== targetId && this.vimeoPlayer) {
              this.vimeoWrapper.dataset.currentVimeoId = targetId;
              // Would need to destroy and recreate player for proper swap
            }
          }
        }, 250);
      });
    }
  }
  
  // Initialize all hero video sections on the page
  function initHeroVideos() {
    const heroSections = document.querySelectorAll(selectors.section);
    
    heroSections.forEach(container => {
      const section = new AutoMazenHeroVideo(container);
      sections.push(section);
    });
  }
  
  // Initialize on DOM content loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeroVideos);
  } else {
    initHeroVideos();
  }
  
  // Create a more robust way to handle variant changes and any other DOM updates that might affect our videos
  
  // Set up MutationObserver to watch for changes to product forms and video elements
  function setupVariantChangeObservers() {
    // Find all product forms on the page
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    
    productForms.forEach(form => {
      // Watch for changes to the form and its children
      const formObserver = new MutationObserver((mutations) => {
        // Variant was likely changed, attempt to restore videos after a brief delay
        setTimeout(() => {
          forceRestoreAllVideos();
        }, 50);
      });
      
      formObserver.observe(form, { 
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'disabled']
      });
      
      // Also attach direct event listeners to common variant selectors
      const variantSelectors = form.querySelectorAll('select, input[type="radio"], .single-option-selector');
      variantSelectors.forEach(selector => {
        selector.addEventListener('change', () => {
          setTimeout(() => {
            forceRestoreAllVideos();
          }, 50);
        });
      });
    });
    
    // Also watch product sections for changes
    const productSections = document.querySelectorAll('.product, .product-section, [data-section-type="product"]');
    productSections.forEach(section => {
      const sectionObserver = new MutationObserver((mutations) => {
        // Product section was modified, check if our videos need to be restored
        setTimeout(() => {
          forceRestoreAllVideos();
        }, 50);
      });
      
      sectionObserver.observe(section, { 
        childList: true,
        subtree: true 
      });
    });
    
    // Direct observation of video elements
    const videoSections = document.querySelectorAll(selectors.section);
    videoSections.forEach(section => {
      const videoContainer = section.querySelector(selectors.videoContainer);
      if (videoContainer) {
        const videoObserver = new MutationObserver((mutations) => {
          // The video container was modified, attempt to restore
          forceRestoreAllVideos();
        });
        
        videoObserver.observe(videoContainer, { 
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['src', 'style', 'class']
        });
      }
    });
  }
  
  // More aggressive function to restore videos by completely reinitializing them
  function forceRestoreAllVideos() {
    const heroVideoSections = document.querySelectorAll(selectors.section);
    heroVideoSections.forEach(section => {
      const sectionId = section.dataset.sectionId;
      const existingIndex = sections.findIndex(sec => sec.sectionId === sectionId);
      
      if (existingIndex !== -1) {
        const existingSection = sections[existingIndex];
        const videoElement = section.querySelector('video');
        
        // Capture current state
        const wasPlaying = existingSection.isPlaying;
        const wasMuted = existingSection.isMuted;
        let currentTime = 0;
        
        // Try to get current time from either our section or directly from the video element
        if (existingSection.video && existingSection.video.currentTime) {
          currentTime = existingSection.video.currentTime;
        } else if (videoElement && videoElement.currentTime) {
          currentTime = videoElement.currentTime;
        }
        
        // Remove old section instance
        sections.splice(existingIndex, 1);
        
        // Create a new instance
        const newSection = new AutoMazenHeroVideo(section);
        sections.push(newSection);
        
        // Force a small delay before restoring state to ensure the video is properly loaded
        setTimeout(() => {
          if (newSection.video) {
            // For HTML5 video
            newSection.video.currentTime = currentTime;
            if (!wasMuted) {
              newSection.unmuteVideo();
            }
            if (wasPlaying) {
              newSection.playVideo();
            }
          } else if (newSection.youtubePlayer) {
            // For YouTube videos
            if (wasPlaying) {
              newSection.youtubePlayer.playVideo();
            }
            if (!wasMuted) {
              newSection.youtubePlayer.unMute();
            }
            if (currentTime > 0) {
              newSection.youtubePlayer.seekTo(currentTime);
            }
          } else if (newSection.vimeoPlayer) {
            // For Vimeo videos
            if (currentTime > 0) {
              newSection.vimeoPlayer.setCurrentTime(currentTime);
            }
            if (!wasMuted) {
              newSection.vimeoPlayer.setVolume(1);
            }
            if (wasPlaying) {
              newSection.vimeoPlayer.play();
            }
          }
        }, 100);
      }
    });
  }
  
  // Listen for Shopify section render events to handle variant changes
  document.addEventListener('shopify:section:load', (event) => {
    const heroVideoSection = event.target.querySelector(selectors.section);
    if (heroVideoSection) {
      const existingIndex = sections.findIndex(section => section.sectionId === heroVideoSection.dataset.sectionId);
      if (existingIndex !== -1) {
        // Remove old section instance
        sections.splice(existingIndex, 1);
      }
      // Initialize the new section
      const sectionInstance = new AutoMazenHeroVideo(heroVideoSection);
      sections.push(sectionInstance);
      
      // Set up the observers again
      setupVariantChangeObservers();
    }
  });
  
  // Handle variant:change event (used by some Shopify themes)
  document.addEventListener('variant:change', () => {
    setTimeout(() => {
      forceRestoreAllVideos();
    }, 50);
  });
  
  // Set up the mutation observers after initial initialization
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setupVariantChangeObservers();
  } else {
    document.addEventListener('DOMContentLoaded', setupVariantChangeObservers);
  }
  
  // Make constructor available globally for section rendering
  window.AutoMazenHeroVideo = AutoMazenHeroVideo;
})();
